import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";
import UserMenu from "@/components/UserMenu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  const navItems = [
    { labelKey: "nav.howItWorks", id: "how-it-works" },
    { labelKey: "nav.giftOptions", id: "gift-options" },
    { labelKey: "nav.occasions", id: "occasions" },
    { labelKey: "nav.faq", id: "faq" },
  ];

  const scrollToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/98 backdrop-blur-sm border-b border-border/40">
      <div className="container mx-auto flex items-center justify-between py-6 px-4">
        <a href="/" className="flex items-center gap-2">
          <Logo />
        </a>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-xs font-light text-muted-foreground hover:text-foreground transition-colors tracking-wide"
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <UserMenu />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs font-light text-muted-foreground tracking-wide"
              onClick={() => navigate("/auth")}
            >
              {t("nav.signIn")}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => navigate("/create")}
            className="rounded-full px-6 bg-foreground text-background hover:bg-foreground/85 text-xs font-light tracking-wide shadow-none"
          >
            {t("nav.createCardCta")}
          </Button>
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-3">
          <LanguageSwitcher />
          <button className="text-foreground" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden border-t border-border/40 bg-background px-4 pb-6">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                scrollToSection(item.id);
                setIsOpen(false);
              }}
              className="block w-full text-start py-3 text-sm font-light text-muted-foreground hover:text-foreground tracking-wide"
            >
              {t(item.labelKey)}
            </button>
          ))}
          <div className="flex flex-col gap-2 pt-4">
            {user ? (
              <button
                onClick={() => {
                  navigate("/dashboard");
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 py-2"
              >
                <UserMenu />
                <span className="text-sm text-foreground">
                  {t("nav.dashboard", { defaultValue: "Dashboard" })}
                </span>
              </button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="font-light"
                onClick={() => {
                  navigate("/auth");
                  setIsOpen(false);
                }}
              >
                {t("nav.signIn")}
              </Button>
            )}
            <Button
              size="sm"
              className="rounded-full bg-foreground text-background hover:bg-foreground/85 font-light shadow-none"
              onClick={() => {
                navigate("/create");
                setIsOpen(false);
              }}
            >
              {t("nav.createCardCta")}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
